
import { Element } from "./element";
import { heckbert, LIM10 } from "../../../lib/nice_num";
import { rem_to_px } from "../px";
import {create_translate_matrix, scale_about_pos, transform_y} from "../../../lib/matrix";


const calc_min_max = data => {

  let mmin = data[0].data[0];
  let mmax = data[0].data[0];

  data.forEach(data_item => {
    for (let i = 0; i < data_item.data.length; i++) {
      mmin = Math.min(mmin, data_item.data[i])
      mmax = Math.max(mmax, data_item.data[i])
    }
  });

  return {
    min: mmin,
    max: mmax,
  };
};

const lerp = (t, from, to) => {
  return (1 - t) * from + t * to;
}

class YAxisRt extends Element {

  constructor(position_cb, data, default_labels) {
    super(position_cb);

    this.setData(data);
    this.default_labels = default_labels;
  }

  setData = (data, default_bounds=undefined) => {
    this.data = data;

    if (default_bounds) {
      this.min = default_bounds[0];
      this.max = default_bounds[1];
    } else {
      const min_max = calc_min_max(data);
      const grow = (min_max.max - min_max.min) * 0.1;

      this.min = min_max.min - grow;
      this.max = min_max.max + grow;
    }
  };

  renderTick = (ctx, label, value, major_tick) => {
    const y = this.valueToYPix(value);
    const tickLength = rem_to_px(0.5);


    ctx.beginPath();
    ctx.moveTo(this.right - tickLength, y);
    ctx.lineTo(major_tick ? this.canvas_width : this.right, y);
    ctx.stroke();

    ctx.textAlign = "left";
    ctx.fillStyle = "#000";
    ctx.fillText(label, this.left, y + rem_to_px(0.2));
  };

  valueToYPix = value => {
    return (value - this.view_min) / (this.view_max - this.view_min)
      * (this.top - this.bottom) + this.bottom;
  }

  pyToNorm = (py, view_matrix_inv) => {
    const py_norm = (py - this.bottom) / (this.top - this.bottom);

    const from_t = transform_y(view_matrix_inv, 0);
    const to_t = transform_y(view_matrix_inv, 1);
    return lerp(py_norm, from_t, to_t);
  }

  pyToValue = (py, view_matrix_inv) => {
    const py_norm = this.pyToNorm(py, view_matrix_inv);
    return lerp(py_norm, this.min, this.max);
  }

  calcScaleMatrix = (grab_y, drag_y, view_matrix_inv) => {
    const scale_mult = 3;
    const y_normalized = this.pyToNorm(grab_y, view_matrix_inv);
    const view_sy = view_matrix_inv.diagonal().get([1]);
    const sy = 1.0 - (drag_y - grab_y) / this.height() * scale_mult * view_sy;
    return scale_about_pos(1., sy, 0, y_normalized);
  }

  calcPanMatrix = (grab_y, drag_y, view_matrix_inv) => {
    const view_sy = view_matrix_inv.diagonal().get([1]);
    const dy = (grab_y - drag_y) / this.height() * view_sy;
    return create_translate_matrix(0, dy);
  }

  genClipRegion = clip_path => {
    clip_path.moveTo(0, 0);
    clip_path.lineTo(this.right, 0);
    clip_path.lineTo(this.right, this.top);
    clip_path.lineTo(this.canvas_width, this.top);
    clip_path.lineTo(this.canvas_width, this.bottom);
    clip_path.lineTo(this.right, this.bottom);
    clip_path.lineTo(this.right, this.canvas_height);
    clip_path.lineTo(0, this.canvas_height);
    clip_path.closePath();
    return clip_path;
  }

  render = (canvas, view_matrix_inv) => {
    this.updatePos();

    // top line
    const ctx = canvas.getContext("2d");

    ctx.save();

    // Clip region
    ctx.clip(this.genClipRegion(new Path2D()), 'evenodd');

    ctx.strokeStyle="#0004";
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(this.right, this.top);
    ctx.lineTo(this.right, this.bottom);
    ctx.stroke();

    const from_t = transform_y(view_matrix_inv, 0);
    const to_t = transform_y(view_matrix_inv, 1);

    this.view_min = lerp(from_t, this.min, this.max);
    this.view_max = lerp(to_t, this.min, this.max);

    let labels = null;

    if (this.default_labels) {
      labels = this.default_labels;
    } else {
      labels = heckbert(this.view_min, this.view_max, 10, LIM10, true);
    }
    labels.forEach(label => {
      this.renderTick(ctx, label.label, label.value, true);
    });

    ctx.restore();
  }
}

export { YAxisRt };
